/**
 * Thành phần dùng chung, render đúng tên class của bản mẫu (`proto.css`).
 * Không thêm style riêng ở đây: muốn đổi giao diện thì sửa bản mẫu rồi chạy lại `extract`.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from "react";

const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).join(" ");

export type ButtonVariant = "dark" | "acc" | "accent" | "soft" | "line" | "danger";
export function Button({
  children,
  variant = "dark",
  size,
  className,
  type = "button",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm" | "lg" | "icon";
}) {
  return (
    <button
      {...props}
      type={type}
      className={cx(
        "btn",
        `btn-${variant === "accent" ? "acc" : variant}`,
        size && `btn-${size}`,
        className,
      )}
    >
      {children}
    </button>
  );
}

export function Card({
  children,
  className,
  tight,
  ...props
}: HTMLAttributes<HTMLElement> & { tight?: boolean }) {
  return (
    <section {...props} className={cx("card", tight && "tight", className)}>
      {children}
    </section>
  );
}

/** Đầu thẻ: tiêu đề + dòng phụ bên trái, nút/chip bên phải. */
export function CardHead({
  title,
  sub,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="card-head">
      <div>
        <h2>{title}</h2>
        {sub && <p className="sub">{sub}</p>}
      </div>
      {children}
    </div>
  );
}

export type ChipTone = "acc" | "good" | "bad" | "warn" | "dark" | "pro";
export function Chip({
  children,
  tone,
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { tone?: ChipTone | undefined }) {
  return (
    <span {...props} className={cx("chip", tone, className)}>
      {children}
    </span>
  );
}

export type Space = "study" | "gram" | "prod" | "note" | "common";
export const SPACE_TAG: Record<Space, { cls: string; label: string }> = {
  study: { cls: "study", label: "Làm bài" },
  gram: { cls: "tgram", label: "Ngữ pháp" },
  prod: { cls: "prod", label: "Năng suất" },
  note: { cls: "tnote", label: "Take note" },
  common: { cls: "common", label: "Chung" },
};
export function SpaceTag({ space }: { space: Space }) {
  const tag = SPACE_TAG[space];
  return <span className={`space-tag ${tag.cls}`}>{tag.label}</span>;
}

/** `.page-head` — mọi màn (trừ Hôm nay) bắt đầu bằng thẻ này. */
export function PageHead({
  space,
  badge,
  title,
  desc,
  children,
}: {
  space?: Space | undefined;
  /** Phần cạnh tag khu, ví dụ huy hiệu MỚI. */
  badge?: ReactNode;
  title: ReactNode;
  desc?: ReactNode;
  /** Hàng tab / nút / chip đặt trong thẻ, dưới mô tả. */
  children?: ReactNode;
}) {
  return (
    <section className="page-head">
      {space && (
        <div className="row">
          <SpaceTag space={space} />
          {badge}
        </div>
      )}
      <h1>{title}</h1>
      {desc && <p>{desc}</p>}
      {children}
    </section>
  );
}

export function Tabs<K extends string>({
  label,
  items,
  active,
  onChange,
  className,
}: {
  label: string;
  items: { key: K; label: ReactNode; title?: string }[];
  active: K;
  onChange: (key: K) => void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div ref={ref} className={cx("tabs", className)} role="tablist" aria-label={label}>
      {items.map((item, index) => (
        <button
          key={item.key}
          type="button"
          role="tab"
          className="tab"
          title={item.title}
          aria-selected={active === item.key}
          tabIndex={active === item.key ? 0 : -1}
          onClick={() => {
            onChange(item.key);
          }}
          onKeyDown={(e) => {
            const next =
              e.key === "ArrowRight"
                ? (index + 1) % items.length
                : e.key === "ArrowLeft"
                  ? (index + items.length - 1) % items.length
                  : e.key === "Home"
                    ? 0
                    : e.key === "End"
                      ? items.length - 1
                      : null;
            const target = next === null ? undefined : items[next];
            if (target) {
              e.preventDefault();
              onChange(target.key);
              ref.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next ?? 0]?.focus();
            }
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

/** `.seg` — chọn một trong ít phương án; thay cho `<select>` của trình duyệt. */
export function Seg<V extends string | number>({
  label,
  options,
  value,
  onChange,
  className,
  style,
}: {
  label: string;
  options: { value: V; label: ReactNode }[];
  value: V;
  onChange: (value: V) => void;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div className={cx("seg", className)} role="group" aria-label={label} style={style}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => {
            onChange(o.value);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Ô chọn kiểu `.input` của bản mẫu, tự dựng thay cho `<select>` của trình duyệt:
 * bấm mở danh sách, chọn bằng chuột hoặc phím ↑ ↓ Enter, Esc để đóng.
 */
export function Select<V extends string | number>({
  label,
  id,
  value,
  options,
  onChange,
  style,
  disabled,
}: {
  label: string;
  id?: string;
  value: V;
  options: { value: V; label: ReactNode }[];
  onChange: (value: V) => void;
  style?: CSSProperties;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const current = options.find((o) => o.value === value);
  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      if (!(e.target instanceof Node) || !box.current?.contains(e.target)) setOpen(false);
    };
    addEventListener("pointerdown", outside);
    box.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.focus();
    return () => {
      removeEventListener("pointerdown", outside);
    };
  }, [open]);
  const move = (e: KeyboardEvent, dir: number) => {
    e.preventDefault();
    const items = [...(box.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    items[Math.max(0, Math.min(items.length - 1, i + dir))]?.focus();
  };
  return (
    <div className="select-wrap" ref={box} style={style}>
      <button
        type="button"
        id={id}
        className="input select-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        onClick={() => {
          setOpen(!open);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span>{current?.label ?? ""}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div
          className="search-results select-list"
          role="listbox"
          aria-label={label}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") move(e, 1);
            if (e.key === "ArrowUp") move(e, -1);
            if (e.key === "Escape") {
              e.preventDefault();
              setOpen(false);
            }
          }}
        >
          {options.map((o) => (
            <button
              key={String(o.value)}
              type="button"
              role="option"
              aria-selected={o.value === value}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Switch({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-label={label}
      aria-checked={checked}
      disabled={disabled}
      className="switch"
      onClick={() => {
        onChange(!checked);
      }}
    />
  );
}

export function Kpi({
  label,
  value,
  sub,
  className,
  style,
  flip,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  className?: string | undefined;
  style?: CSSProperties;
  /** Số đứng trên nhãn (kiểu ô số ở thẻ Tập trung). */
  flip?: boolean;
}) {
  return (
    <div className={cx("kpi", className)} style={style}>
      {flip ? (
        <>
          <span className="k-val num">{value}</span>
          <span className="k-lbl">{label}</span>
        </>
      ) : (
        <>
          <span className="k-lbl">{label}</span>
          <span className="k-val num">{value}</span>
        </>
      )}
      {sub !== undefined && <span className="k-sub">{sub}</span>}
    </div>
  );
}

/** `.pbar` — thanh tiến độ mảnh, `pct` từ 0 đến 100. */
export function Pbar({
  pct,
  style,
  label,
}: {
  pct: number;
  style?: CSSProperties;
  label?: string;
}) {
  const p = Math.max(0, Math.min(100, pct));
  return (
    <div
      className="pbar"
      style={style}
      role={label ? "progressbar" : undefined}
      aria-label={label}
      aria-valuenow={label ? Math.round(p) : undefined}
    >
      <i style={{ width: `${p}%` }} />
    </div>
  );
}

/** Vòng tiến độ SVG (`ringSVG` của bản mẫu). */
export function Ring({
  pct,
  sw = 10,
  track = "var(--surface-3)",
  color = "var(--accent)",
}: {
  pct: number;
  sw?: number;
  track?: string;
  color?: string;
}) {
  const r = 50 - sw / 2 - 1;
  const p = Math.max(0, Math.min(100, pct));
  return (
    <svg className="ring-svg" viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="50" cy="50" r={r} fill="none" style={{ stroke: track }} strokeWidth={sw} />
      <circle
        cx="50"
        cy="50"
        r={r}
        fill="none"
        style={{ stroke: color }}
        strokeWidth={sw}
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray={`${p} 100`}
        strokeOpacity={p > 0 ? 1 : 0}
        transform="rotate(-90 50 50)"
      />
    </svg>
  );
}

/** Cá vàng — mascot SVG của bản mẫu (`fishSVG`). `pose`: ngủ khi nghỉ, ăn mừng khi xong hiệp. */
export function Fish({
  className,
  pose,
  logo,
}: {
  className?: string;
  pose?: "sleep" | "party" | undefined;
  logo?: boolean;
}) {
  const style = logo
    ? ({ "--fish-fin": "#5B47C9", "--fish-fin2": "#4A38B0" } as CSSProperties)
    : undefined;
  return (
    <svg
      className={cx("fish", pose, className)}
      viewBox="0 0 220 180"
      role="img"
      aria-label="Cá, mascot của Cá Toeic"
      style={style}
    >
      <g className="fish-all">
        <g className="fish-tail">
          <path
            d="M64 94 C46 72 26 58 10 60 C19 79 19 109 10 128 C27 130 46 116 64 98 Z"
            style={{ fill: "var(--fish-fin)" }}
          />
        </g>
        <path
          d="M88 46 C98 18 132 12 152 34 C134 34 114 40 101 54 Z"
          style={{ fill: "var(--fish-fin)" }}
        />
        <ellipse cx="118" cy="96" rx="68" ry="57" style={{ fill: "var(--fish-body)" }} />
        <path
          d="M66 116 C84 146 152 152 180 114 C160 128 96 130 66 116 Z"
          style={{ fill: "var(--fish-belly)" }}
        />
        <path
          d="M82 80 q11 11 0 22 M97 70 q11 11 0 22 M97 98 q11 11 0 22"
          style={{ stroke: "var(--fish-line)" }}
          strokeWidth="3.2"
          fill="none"
          strokeLinecap="round"
          opacity=".45"
        />
        <g className="fish-fin">
          <path
            d="M112 110 C100 128 106 144 124 141 C129 130 127 117 120 109 Z"
            style={{ fill: "var(--fish-fin2)" }}
          />
        </g>
        <ellipse cx="152" cy="110" rx="10" ry="6.5" fill="#F4A3BA" opacity=".8" />
        <g className="fish-eye-open">
          <circle cx="146" cy="82" r="15" fill="#16151A" />
          <circle cx="151.5" cy="76" r="5.5" fill="#fff" />
          <circle cx="140.5" cy="88" r="2.4" fill="#fff" />
        </g>
        <path
          className="fish-eye-closed"
          d="M132 84 Q146 95 160 84"
          stroke="#16151A"
          strokeWidth="4.5"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M171 104 Q178 111 185 103"
          stroke="#16151A"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
        <ellipse
          cx="96"
          cy="64"
          rx="15"
          ry="7"
          fill="#fff"
          opacity=".38"
          transform="rotate(-22 96 64)"
        />
      </g>
      <g
        className="zzz"
        fill="currentColor"
        fontFamily="Plus Jakarta Sans, system-ui, sans-serif"
        fontWeight="800"
      >
        <text x="178" y="50" fontSize="22">
          z
        </text>
        <text x="196" y="30" fontSize="16">
          z
        </text>
      </g>
    </svg>
  );
}

/** Logo: Cá trên nền tím bo góc (`logoHTML`). */
export function Logo({ size }: { size?: number }) {
  const style = size
    ? { width: size, height: size, borderRadius: Math.round(size / 3.2) }
    : undefined;
  return (
    <span className="logo" style={style}>
      <Fish logo />
    </span>
  );
}

/**
 * Hộp thoại: `.scrim > .modal` của bản mẫu, dựng trên `<dialog>` để có sẵn Esc, khoá focus
 * và vai trò dialog cho trình đọc màn hình.
 */
export function Modal({
  title,
  children,
  onClose,
  wide = false,
  className,
  hideTitle = false,
  head,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  className?: string;
  /** Ẩn h2 (vẫn giữ tên hộp thoại) khi nội dung tự có tiêu đề riêng. */
  hideTitle?: boolean;
  /** Thay h2 ở đầu hộp bằng nội dung khác (ví dụ tag khu). */
  head?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useLayoutEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => {
      dialog?.close();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="scrim"
      aria-label={title}
      onCancel={(e) => {
        e.preventDefault();
        close.current();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) close.current();
      }}
    >
      <div className={cx("modal", wide && "wide", className)}>
        <div className="modal-head">
          {head ?? (hideTitle ? <span /> : <h2>{title}</h2>)}
          <button type="button" className="xbtn" aria-label="Đóng" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </dialog>
  );
}

export interface ToastItem {
  id: number;
  message: string;
  icon?: string | undefined;
}
/** `#toasts` — tối đa 3 thông báo mới nhất ở giữa đáy màn hình. */
export function Toasts({ items }: { items: ToastItem[] }) {
  return (
    <div id="toasts" role="status" aria-live="polite">
      {items.slice(-3).map((t) => (
        <div key={t.id} className="toast">
          <span className="t-ic" aria-hidden="true">
            {t.icon ?? "🐠"}
          </span>
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}
/** Một thông báo đơn lẻ, dùng khi màn tự quản lý trạng thái hiển thị. */
export function Toast({ children }: { children: ReactNode }) {
  return (
    <div id="toasts" role="status" aria-live="polite">
      <div className="toast">
        <span className="t-ic" aria-hidden="true">
          🐠
        </span>
        <span>{children}</span>
      </div>
    </div>
  );
}

/**
 * `#tip` — chú thích nổi cho mọi phần tử có `data-tip="Tiêu đề|dòng|#N dòng có màu"`.
 * Gắn một lần ở gốc app.
 */
export function TipLayer() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const tip = ref.current;
    if (!tip) return;
    const show = (el: Element, px?: number, py?: number) => {
      const s = el.getAttribute("data-tip");
      if (!s) return;
      tip.textContent = "";
      s.split("|").forEach((line, i) => {
        const d = document.createElement("div");
        const m = /^#(\d) (.*)$/.exec(line);
        if (m) {
          const k = document.createElement("span");
          k.className = `tk b${m[1] ?? "0"}`;
          d.append(k, document.createTextNode(m[2] ?? ""));
        } else if (i === 0) {
          const b = document.createElement("b");
          b.textContent = line;
          d.append(b);
        } else d.textContent = line;
        tip.append(d);
      });
      tip.classList.add("show");
      if (px === undefined || py === undefined) {
        const r = el.getBoundingClientRect();
        px = r.left + r.width / 2;
        py = r.top;
      }
      const tw = tip.offsetWidth,
        th = tip.offsetHeight;
      let x = px + 14,
        y = py - th - 12;
      if (x + tw > innerWidth - 8) x = px - tw - 14;
      if (x < 8) x = 8;
      if (y < 8) y = py + 18;
      tip.style.left = `${x}px`;
      tip.style.top = `${y}px`;
    };
    const hide = () => {
      tip.classList.remove("show");
    };
    const target = (e: Event) =>
      e.target instanceof Element ? e.target.closest("[data-tip]") : null;
    const move = (e: PointerEvent) => {
      const el = target(e);
      if (el) show(el, e.clientX, e.clientY);
      else hide();
    };
    const focus = (e: FocusEvent) => {
      const el = target(e);
      if (el) show(el);
    };
    addEventListener("pointermove", move, { passive: true });
    addEventListener("focusin", focus);
    addEventListener("focusout", hide);
    addEventListener("scroll", hide, { passive: true });
    return () => {
      removeEventListener("pointermove", move);
      removeEventListener("focusin", focus);
      removeEventListener("focusout", hide);
      removeEventListener("scroll", hide);
    };
  }, []);
  return <div id="tip" role="tooltip" ref={ref} />;
}
/** Giữ API cũ: bọc phần tử con bằng `data-tip`. */
export function Tooltip({ text, children }: { text: string; children: ReactNode }) {
  return <span data-tip={text}>{children}</span>;
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Card className="stack">
      <h2>{title}</h2>
      {children && <div className="muted">{children}</div>}
    </Card>
  );
}
export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card className="stack">
      <div role="alert">{message}</div>
      <div>
        <Button variant="soft" onClick={onRetry}>
          Thử lại
        </Button>
      </div>
    </Card>
  );
}
export function Skeleton() {
  return (
    <div role="status" className="card faint">
      Đang tải…
    </div>
  );
}
export function Field({
  label,
  id,
  children,
  error,
}: {
  label: string;
  id: string;
  children: ReactNode;
  error?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
